import React, { FC, useCallback, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { TaskProgressDialog } from 'pages/Funds/components/ProcessManagement/TaskProgressDialog';
import theme from 'theme';
import { GREEN } from 'theme/prismDesignTokens';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import useStyles from './styles';
import type { CustomTaskBoxProps } from './types';

const CustomTaskBox: FC<CustomTaskBoxProps> = props => {
  const {
    cellKey,
    children,
    companyName,
    fundName,
    task,
    measurementDateName,
    companySlug,
    isDocuments = false,
    onTaskClick,
  } = props;
  const { progress } = task || {};
  const { completed, total } = getObjectValue(progress);

  const [open, setOpen] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);

  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { firmInfo } = storeValue;

  const percentageOfProgress = (getNumberValue(task?.progress?.completed) / (task?.progress?.total ?? 1)) * 100;
  const taskWasCompleted = percentageOfProgress === 100;

  const backgroundColor = taskWasCompleted ? GREEN[400] : theme.palette.pink;
  const borderColor = taskWasCompleted ? GREEN[400] : theme.palette.pink;
  const color = theme.palette.white;
  const classes = useStyles({ color, backgroundColor, borderColor });

  const stopSpreadsheetEvent = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenTooltip(false);

    if (onTaskClick) {
      onTaskClick({
        cellKey,
        companyName,
        fundName,
        task,
        measurementDateName,
        companySlug,
        isDocuments,
      });
      return;
    }

    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleMouseEnter = () => {
    if (!open) {
      setOpenTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setOpenTooltip(false);
  };

  return (
    <Tooltip
      title={`${completed}/${total}`}
      placement="top"
      arrow
      disableFocusListener
      disableTouchListener
      open={openTooltip}
      onOpen={handleMouseEnter}
      onClose={handleMouseLeave}>
      <div
        role="presentation"
        className={classes.root}
        onClick={stopSpreadsheetEvent}
        onDoubleClick={stopSpreadsheetEvent}
        onKeyDown={stopSpreadsheetEvent}
        onMouseDown={stopSpreadsheetEvent}
        onPointerDown={stopSpreadsheetEvent}>
        <IconButton
          className={classes.svgIcon}
          onClick={onClick}>
          {children}
        </IconButton>

        {open && (
          <TaskProgressDialog
            open={open}
            onClose={onClose}
            cellKey={cellKey}
            companyName={companyName}
            fundName={fundName}
            firmName={getStringValue(firmInfo?.name)}
            task={task}
            measurementDateName={measurementDateName}
            companySlug={companySlug}
            isDocuments={isDocuments}
          />
        )}
      </div>
    </Tooltip>
  );
};

export default CustomTaskBox;
